import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lohi/environments/environment';

@Injectable({
  providedIn: 'root',
})
export class AssetsService {
  constructor(private httpClient: HttpClient) {}

  // Map frontend column names to backend field names
  private mapSortFieldToApiField(sortField: string): string {
    const fieldMapping: { [key: string]: string } = {
      driverName: 'driver_name',
      phoneNumber: 'phone_number',
      companyName: 'company_name',
      scheduledAt: 'scheduled_at',
      assetName: 'asset_name',
      pickupSiteName: 'pickup_site_name',
      sufficientEscrow: 'sufficient_escrow',
      status: 'status',
    };

    return fieldMapping[sortField] || sortField;
  }

  public async allAssets$(
    pageIndex: number,
    pageSize: number,
    sortDirection: string,
    sortField: string,
    searchTerm?: string,
  ): Promise<any> {
    let params = new HttpParams().set('page', pageIndex.toString()).set('limit', pageSize.toString());

    if (sortField) {
      // Map frontend column name to backend field name
      const apiSortField = this.mapSortFieldToApiField(sortField);
      params = params.set('sortField', apiSortField);

      // Handle empty sort direction - default to ASC
      const direction = sortDirection || 'ASC';
      params = params.set('sortOrder', direction);
    }

    // Add search parameter if provided
    if (searchTerm && searchTerm.trim() !== '') {
      params = params.set('search', searchTerm.trim());
    }

    try {
      const response = await this.httpClient
        .get<any>(`${environment.api}/v2/amp/asset/scheduled_pickups`, { params })
        .toPromise();
      console.log('API Response:', response);

      // Check the structure of the response for debugging
      const data = response?.data || [];
      const totalCount = response?.totalCount || response?.total || response?.pagination?.total_items || 0;
      const totalPages =
        response?.totalPages || response?.pagination?.total_pages || Math.ceil(totalCount / pageSize) || 0;
      const currentPage = response?.currentPage || response?.pagination?.current_page || pageIndex || 0;

      console.log('Extracted pagination:', { data, totalCount, totalPages, currentPage, searchApplied: !!searchTerm });

      // Ensure response has the expected structure
      return {
        data,
        totalCount,
        totalPages,
        currentPage,
      };
    } catch (error) {
      console.error('Error fetching assets:', error);
      return { data: [], totalCount: 0, totalPages: 0, currentPage: 0 };
    }
  }

  public async getAvailableAssets(assetTypes?: string[]): Promise<any> {
    try {
      let params = new HttpParams();

      // Add asset types as query parameters if provided
      if (assetTypes && assetTypes.length > 0) {
        assetTypes.forEach((type) => {
          params = params.append('type', type);
        });
      }

      const response = await this.httpClient
        .get<any>(`${environment.api}/v2/amp/assets`, { params })
        .toPromise();

      return {
        data: response,
      };
    } catch (error) {
      console.error('Error fetching available assets:', error);
      return { data: [] };
    }
  }

  public async updateScheduledPickupAsset(scheduleId: number, assetId: number): Promise<any> {
    try {
      const payload = {
        scheduleID: Number(scheduleId),
        assetID: Number(assetId),
      };

      const response = await this.httpClient
        .put<any>(`${environment.api}/v2/amp/asset/update_scheduled_pickup_asset`, payload)
        .toPromise();

      return response;
    } catch (error) {
      console.error('Error updating scheduled pickup asset:', error);
      throw error; // Rethrow to handle in the component
    }
  }

  public async archiveScheduledPickup(scheduleId: number, reason: string): Promise<any> {
    try {
      const payload = {
        scheduleID: Number(scheduleId),
        reason,
      };

      return await this.httpClient
        .put<any>(`${environment.api}/v2/amp/asset/archive_scheduled_pickup`, payload)
        .toPromise();
    } catch (error) {
      console.error('Error archiving scheduled pickup:', error);
      throw error; // Rethrow to handle in the component
    }
  }
}
